import { css } from '@emotion/react'
import { Color, Spacing, Theme, Typography, ZIndex } from '@opallabs/gem'
import { buttonSelectors } from '@opallabs/lapidary'

export const readOnlyWrapper = (tooltipped: boolean, theme: Theme) =>
  css({
    // The value's own type and ink, so a field reads the same on a page whose
    // body text is set differently — the read-only field has no button to
    // carry them for it.
    ...Typography.T500,
    alignItems: 'center',
    color: Color[theme]['Text/Primary'],
    columnGap: Spacing.XS,
    minBlockSize: 36,
    display: 'flex',
    paddingInline: Spacing.S + 1,

    '&:hover': {
      cursor: tooltipped ? 'pointer' : 'text',
    },
  })

export const readOnly = ({
  hasValue,
  lineClamp,
  theme,
}: {
  hasValue: boolean
  lineClamp?: number
  theme: Theme
}) =>
  css({
    color: hasValue
      ? Color[theme]['Text/Primary']
      : Color[theme]['Text/Secondary'],
    display: '-webkit-box',
    lineHeight: '21px',
    overflow: 'hidden',
    WebkitLineClamp: lineClamp,
    WebkitBoxOrient: 'vertical',
  })

export const buttonStyles = ({
  hasLeadingNode = false,
  lineClamp,
  theme,
  colorsEnabled,
}: {
  hasLeadingNode?: boolean
  lineClamp?: number
  theme: Theme
  colorsEnabled?: boolean
}) =>
  css({
    blockSize: 'auto',
    columnGap: Spacing.XS,
    justifyContent: 'start',
    maxBlockSize: lineClamp ? 20 * lineClamp + 16 : 'unset',
    minBlockSize: 36,
    padding: colorsEnabled ? `${Spacing.XS}px ${Spacing.S}px` : Spacing.S,
    width: '100%',

    '&:hover:enabled:before': {
      backgroundColor: Color[theme]['Color/Surface/Surface-2'],
    },

    // Only the Button's contents wrapper: a descendant rule would beat the
    // ColorLabelToken's own styles and break its colors and ellipsis.
    [`.${buttonSelectors.buttonContents}`]: {
      // A clamped box stacks each element in it on its own line, so a mark in
      // front of the value needs a row. The value's own text clamps itself.
      ...(hasLeadingNode
        ? { alignItems: 'center', columnGap: Spacing.XS, display: 'flex' }
        : { display: '-webkit-box' }),
      maxInlineSize: '100%',
      overflow: 'hidden',
      textTransform: 'none',
      textAlign: 'left',
      textWrap: 'wrap',
      WebkitLineClamp: lineClamp,
      WebkitBoxOrient: 'vertical',
      zIndex: ZIndex.Element.Level1,
    },
  })

export const compactLineHeight = css({ lineHeight: '18px' })

// Overrides the token's `inherit`, so a long label truncates instead of clipping.
export const colorToken = css({ maxInlineSize: '100%' })

export const unavailable = css({ opacity: 0.5 })
